// NOTE: This LanguageContext is intentionally duplicated between @appic/hooks and
// @appic/ui-components to avoid circular dependency. Both are identical.
// If you modify one, update the other.
// See also: packages/core/hooks/src/contexts/LanguageContext.tsx

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { de, en, nl, fr, es, pt, zh, tr, tlh, ja, vi, km } from '@appic/locales'

export type Language = 'de' | 'en' | 'nl' | 'fr' | 'es' | 'pt' | 'zh' | 'tr' | 'tlh' | 'ja' | 'vi' | 'km'

interface LanguageContextType {
  language: Language
  setLanguage: (lang: Language) => void
  t: (key: string, params?: Record<string, string | number>) => string
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

const VALID_LANGUAGES: Language[] = ['de', 'en', 'nl', 'fr', 'es', 'pt', 'zh', 'tr', 'tlh', 'ja', 'vi', 'km']

// Combined translations object
const translations = {
  de,
  en,
  nl,
  fr,
  es,
  pt,
  zh,
  tr,
  tlh,
  ja,
  vi,
  km
}

// Translation function with parameter substitution.
//
// Fallback chain: requested-language → DE (canonical per guard-i18n-missing-keys)
// → key string. The DE fallback exists because de.ts is the authoring locale
// for "DE-only" surfaces (e.g. CLANDA onboarding) — features that ship strings
// only in de.ts must STILL render correctly on browsers detected as en/fr/etc.
// Without the DE fallback those features render raw i18n keys (regression
// class: MO-CLANDA-ONBOARDING-V1 P1 caught by Commander 2026-05-21).
const translate = (key: string, language: Language, params?: Record<string, string | number>): string => {
  let translation = translations[language][key as keyof typeof translations[typeof language]]

  if (!translation && language !== 'de') {
    translation = translations.de[key as keyof typeof translations.de]
    if (translation) {
      console.warn(`Translation missing for key: ${key} in language: ${language}, fell back to de`)
    }
  }

  if (!translation) {
    console.warn(`Translation missing for key: ${key} in language: ${language} (and de fallback)`)
    return key // Return the key if translation is missing in BOTH requested locale AND de.
  }

  if (params) {
    return Object.entries(params).reduce((str, [paramKey, paramValue]) => {
      return str.replace(new RegExp(`{{${paramKey}}}`, 'g'), String(paramValue))
    }, translation)
  }

  return translation
}

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Detect browser language on initial mount
  const detectBrowserLanguage = (): Language => {
    if (typeof window === 'undefined') return 'de'
    
    // Check localStorage first (user preference)
    const savedLanguage = localStorage.getItem('language') as Language
    if (savedLanguage && VALID_LANGUAGES.includes(savedLanguage)) {
      return savedLanguage
    }
    
    // Detect from browser/system (match navigator.language prefix)
    const browserLang = navigator.language || (navigator as Navigator & { userLanguage?: string }).userLanguage
    if (browserLang) {
      const langCode = browserLang.toLowerCase().split('-')[0]
      if (langCode === 'nl') return 'nl'
      if (langCode === 'fr') return 'fr'
      if (langCode === 'es') return 'es'
      if (langCode === 'pt') return 'pt'
      if (langCode === 'zh') return 'zh'
      if (langCode === 'tr') return 'tr'
      if (langCode === 'ja') return 'ja'
      if (langCode === 'de') return 'de'
      if (langCode === 'en') return 'en'
      if (langCode === 'vi') return 'vi'
      if (langCode === 'km') return 'km'
    }
    
    // Default to German
    return 'de'
  }

  const [language, setLanguage] = useState<Language>(detectBrowserLanguage())

  // Load language preference from localStorage on mount (if not already set by detection)
  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') as Language
    if (savedLanguage && VALID_LANGUAGES.includes(savedLanguage)) {
      setLanguage(savedLanguage)
    }
  }, [])

  // Save language preference to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('language', language)
  }, [language])

  const t = (key: string, params?: Record<string, string | number>) => {
    return translate(key, language, params)
  }

  const value: LanguageContextType = {
    language,
    setLanguage,
    t
  }

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  )
}

/** Fallback when used outside LanguageProvider (e.g. portaled Dialog) so app does not crash. */
const fallbackLanguageContext: LanguageContextType = {
  language: 'de',
  setLanguage: () => {},
  t: (key: string) => key,
}

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext)
  if (context === undefined) {
    return fallbackLanguageContext
  }
  return context
}